/* eslint-disable react-hooks/exhaustive-deps */
import { SearchIcon } from '@chakra-ui/icons';
import { Box, Button, Center, Divider, HStack, Text } from '@chakra-ui/react';
import { CQTreeTable, TextInput } from '@laxmimanogna/code-quick-components';
import { createColumnHelper } from '@tanstack/react-table';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppColors from '../../../constants/AppColors';
import ROUTES from '../../../constants/Routes';
import { FONT_FAMILY } from '../../../constants/Theme';
import { AuthContext } from '../../../providers/AuthProvider';
import { DashboardContext } from '../../../providers/DashboardProvider';
import { replaceRoute, toTitleCase } from '../../../utils/common.utils';
import { getCurrentUserTypeIdObject } from '../../../utils/getCurrentUserTypeDetails';
import { defaultTreeTableProp, paginationProps } from './table_config/cq_tree_table_props';
import { priceConverter } from '../../../utils/priceConverter';
import InfoTooltip from '../../../components/InfoTooltip';

const PROVIDER_SUBHEADER = { bg: '#F1F5F9', color: '#64748B', };
const columnHelper = createColumnHelper();

function ViewButton({ onClick }) {
  return (
    <Button size="sm" bg={AppColors.secondary} color="white" borderRadius="3xl" px={4}
      fontWeight="normal" fontSize="sm"
      _hover={{ transition: 'all .1s ease', bgColor: AppColors.white,
        color: AppColors.secondary, outline: `2px solid ${AppColors.secondary}` }}
      onClick={onClick}>View</Button>
  );
}
const NA = () => <Text color={AppColors.gray} textAlign="center">N/A</Text>;

function DepartmentTable({ hospitalId = null }) {
  const tableRef = useRef(null);
  const dashboardContext = useContext(DashboardContext);
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const { treeData, isHierarchyLoading, getHierarchyTree, hospitalFilter, setHospitalFilter } = dashboardContext;
  const [searchText, setSearchText] = useState('');
  const [ordering, setOrdering]     = useState('');

  useEffect(() => {
    const obj = getCurrentUserTypeIdObject(authContext.currentUser);
    setHospitalFilter(prev => ({ ...prev, ...obj }));
  }, [authContext.currentUser]);

  useEffect(() => {
    if (hospitalFilter.hospital_id || hospitalId) {
      getHierarchyTree('hospital', hospitalFilter.hospital_id || hospitalId, {
        search: hospitalFilter?.search || '',
        ordering,
      });
    }
  }, [hospitalFilter, ordering, hospitalId]);

  const onFilterChange = e => setSearchText(e ? e.target.value : '');
  const onSearchAction = () => setHospitalFilter(prev => ({ ...prev, search: searchText }));

  // Depth 0 = department, depth 1 = provider
  const columns = useMemo(() => [

    columnHelper.accessor('name', {
      id: 'name',
      header: () => 'Department',
      subHeader: {
        1: { label: 'Provider', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const depth = info.row.depth;
        const val   = info.getValue();
        if (depth === 1) {
          const { first_name, last_name } = info.row.original;
          return <Text fontWeight={500} textAlign="start" fontSize="13px" color="green.700">{val || toTitleCase(`${first_name ?? ''} ${last_name ?? ''}`.trim())}</Text>;
        }
        return <Text fontWeight={600} textAlign="start" fontSize="14px" color={AppColors.secondary}>{toTitleCase(val)}</Text>;
      },
    }),

    columnHelper.accessor(row => row, {
      id: 'active_audits',
      header: () => 'Active Audits',
      subHeader: {
        1: { label: 'Total Uploads', ...PROVIDER_SUBHEADER, sortable: true },
      },
      sortValue: row => row.active_audits ?? row.total_uploads ?? null,
      cell: info => {
        const row   = info.getValue();
        const depth = info.row.depth;
        const val   = depth === 1 ? row.total_uploads : row.active_audits;
        return val != null ? <Text textAlign="center" fontSize="13px">{val}</Text> : <NA />;
      },
    }),

    columnHelper.accessor('chart_accuracy', {
      id: 'chart_accuracy',
      header: () => 'Chart Accuracy',
      subHeader: {
        1: { label: 'Chart Accuracy', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{val}%</Text> : <NA />;
      },
    }),

    columnHelper.accessor('cq_rev_opp', {
      id: 'cq_rev_opp',
      header: () => 'RI Rev. Opp',
      subHeader: {
        1: { label: 'RI Rev. Opp', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return val != null ? <Text textAlign="center" fontSize="13px">{priceConverter(val)}</Text> : <NA />;
      },
    }),

    columnHelper.accessor('organization_rank', {
      id: 'organization_rank',
      header: () => null,
      enableSorting: false,
      subHeader: {
        1: { label: 'Org. Rank', ...PROVIDER_SUBHEADER, sortable: true },
      },
      cell: info => {
        const val = info.getValue();
        return (info.row.depth === 1 && val != null)
          ? <Text textAlign="center" fontSize="13px">{val}</Text>
          : '';
      },
    }),

    columnHelper.accessor(row => row.id, {
      id: 'action',
      header: () => '',
      enableSorting: false,
      cell: info => {
        const row   = info.row.original;
        const depth = info.row.depth;
        if (depth === 0) {
          return <ViewButton onClick={() => navigate(
            replaceRoute(hospitalId ? ROUTES.DEPARTMENT_DASHBOARD : ROUTES.HOSPITAL_DEPARTMENT_DASHBOARD, { hospitalId: row.hospital_id, departmentId: row.id }),
            { state: row.name }
          )} />;
        }
        // depth 1: provider
        return <ViewButton onClick={() => navigate(replaceRoute(hospitalId ? ROUTES.PHYSICIAN_DASHBOARD : ROUTES.HOSPITAL_PHYSICIAN_DASHBOARD , {
          departmentId: row.department_id ?? hospitalFilter.department_id,
          hospitalId:   hospitalId ?? 1,
          physicianId:  row.id,
        }))} />;
      },
    }),

  ], [hospitalFilter, navigate]);

  return (
    <Box marginTop={3} width="100%" bg={AppColors.white} padding={4} borderRadius="lg" shadow="md">
      <HStack width="100%" justifyContent="space-between" mb={3}>
        <HStack spacing={2}>
          <Text fontFamily={FONT_FAMILY.ptSans} color={AppColors.primary}>Departments</Text>
          <InfoTooltip infoContent={
            <p><b>Departments</b> — expand each department to see its providers. Click View to access further details, providing a comprehensive overview of each facility's performance and metrics.</p>
          } />
        </HStack>
        <Box>
          <TextInput
            borderRadius="md"
            onKeyDown={e => { if (e.keyCode === 13) onSearchAction(); }}
            onChange={e => onFilterChange(e)}
            rightIconProps={{ zIndex: 0 }}
            placeholder="Search Departments"
            rightIcon={<SearchIcon color={AppColors.gray} />}
          />
        </Box>
      </HStack>
      <Divider />
      <Box
        id="fixedTableHeader"
        maxHeight={'400px'}
        overflowY={'scroll'}
        className="tableWraper"
      >
        <CQTreeTable
          ref={tableRef}
          searchText={searchText}
          data={treeData ?? []}
          columns={columns}
          getSubRows={row => row.subRows}
          isLoading={isHierarchyLoading}
          manualSortBy={false}
          size={9999}
          {...defaultTreeTableProp}
          onAsc={h  => setOrdering(h)}
          onDesc={h => setOrdering(`-${h}`)}
        />
      </Box>

      {!isHierarchyLoading && !treeData?.length && (
        <Center mt={10} fontFamily={FONT_FAMILY.ptSans}>No data available</Center>
      )}
    </Box>
  );
}

export default DepartmentTable;